import { AnyAction, createAsyncThunk, createSlice, ThunkDispatch } from '@reduxjs/toolkit';
import { IVictoryCeremonyList, TimeTableEventTabType } from '@App/types';
import { RootState } from '@App/store';

import victoryCeremonyApi from '@App/services/victoryCeremony/victoryCeremonyApi';
import { setActiveTab } from '@Features/timeTableSelectedEvent/timeTableSelectedEventSlice';
import { setSelectedTimeTableId } from '@Features/timeTable/timeTableSlice';
import { eventsApi } from '@App/services/events/eventsApi';
import { pollingSucceeded, pollingFailed } from '@Features/errorHandler/polling/pollingErrorHandlerSlice';

import { pollingErrorHandlerSlice } from '@Features/errorHandler/polling/pollingErrorHandlerSlice';

export interface IVictoryCeremonyState {
  changeTimestamp: number; // Tohle trigeruje mount/unmout pooling kontejneru, tak aby se forsnul reload při znovu kliku (jinak se vysypal state)
  dataLoaded: boolean;
  isFetching: boolean;
  error: any;
  data: IVictoryCeremonyList | null;
}

const initialState: IVictoryCeremonyState = {
  changeTimestamp: 0,
  dataLoaded: false,
  isFetching: false,
  error: null,
  data: null,
};

export const getVictroyCeremonyDataAsync = createAsyncThunk(
  'victoryCeremony/fetchData',
  async (timeTableId: number, { dispatch, rejectWithValue, signal }) => {
    try {
      const response = await victoryCeremonyApi.fetchVictoryCeremonyData(timeTableId, signal);

      const thunkDispatch = dispatch as ThunkDispatch<any, any, AnyAction>; // Nutno to přetypovat, TypeScript si s tím nějak
      thunkDispatch(eventsApi.util.updateQueryData('getEventsStates', '', draft => {
        draft.states[timeTableId] = response.eventState;
      }));

      dispatch(pollingSucceeded());

      return response.data;
    } catch (err) {
      dispatch(pollingFailed({ sectionName: 'VictroyCeremony', error: err }));
      return rejectWithValue(err);
    }
  }
);

export const victoryCeremonySlice = createSlice({
  name: 'victoryCeremonySlice',
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(getVictroyCeremonyDataAsync.pending, state => {
        state.isFetching = true;
      })
      .addCase(getVictroyCeremonyDataAsync.fulfilled, (state, action) => {
        state.isFetching = false;
        state.dataLoaded = true;
        state.error = null;
        state.data = action.payload;
      })
      .addCase(getVictroyCeremonyDataAsync.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.isFetching = false;
          state.error = action.payload;
        }
      })
      .addCase(setActiveTab, (state, action) => { // Clear state
        if (action.payload === TimeTableEventTabType.VictoryCeremony) {
          state.changeTimestamp = Date.now();
        } else {
          Object.assign(state, initialState);
        }
      })
      .addCase(setSelectedTimeTableId, (state, action) => {
        if (action.payload.hasChanged) {
          Object.assign(state, initialState);
        } else {
          state.changeTimestamp = Date.now();
        }
      })
      .addCase(eventsApi.internalActions.unsubscribeQueryResult, state => { // Clear state when movind to different screen
        Object.assign(state, initialState);
      })
      .addCase(pollingErrorHandlerSlice.actions.clearPollingErrors, (state) => {
        state.error = null;
      });
  }
});

export const selectIsFetching = (state: RootState) => state.victoryCeremony.isFetching;
export const selectError = (state: RootState) => state.victoryCeremony.error;
export const selectDataLoaded = (state: RootState) => state.victoryCeremony.dataLoaded;
export const selectData = (state: RootState) => state.victoryCeremony.data;
export const selectChangeTimestamp = (state: RootState) => state.victoryCeremony.changeTimestamp;

export default victoryCeremonySlice.reducer;